/** HTTP clients for the auth endpoints (activation wizard, SSO handoff, token refresh). */

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, finalize, shareReplay, throwError } from 'rxjs';
import { CommonService } from 'src/@services/common.service';
import { AuthEndpoints } from 'src/@shared/endpoints/endpoints';
import { environment } from 'src/environments/environment';
import {
  ActivationIdentityRequest,
  ActivationFinish,
  ActivationStatus,
  ApiEnvelope,
  PasskeyOptionsResult,
} from './auth.models';
import { ResponseStatus } from 'src/@shared/enum';
import { AuthServiceService } from 'src/app/auth-service.service';

@Injectable({
  providedIn: 'root',
})
export class ActivationApiService {
  constructor(private _cS: CommonService, private http: HttpClient) {}

  // CommonService.API_GET refuses to run without a bearer token; activation is anonymous by design.
  status(token: string): Observable<ApiEnvelope<ActivationStatus>> {
    return this.http.get<ApiEnvelope<ActivationStatus>>(
      `${environment.apiURL}${AuthEndpoints.ActivationStatus}?token=${encodeURIComponent(token)}`
    );
  }

  verifyIdentity(body: ActivationIdentityRequest): Observable<ApiEnvelope<ActivationStatus>> {
    return this._cS.API_POST(AuthEndpoints.ActivationVerifyIdentity, body, false);
  }

  verifyPhone(token: string, code: string, siteType: string): Observable<ApiEnvelope<ActivationStatus>> {
    return this._cS.API_POST(AuthEndpoints.ActivationVerifyPhone, { token, code, siteType }, false);
  }

  verifyEmail(token: string, code: string, siteType: string): Observable<ApiEnvelope<ActivationStatus>> {
    return this._cS.API_POST(AuthEndpoints.ActivationVerifyEmail, { token, code, siteType }, false);
  }

  resend(token: string, siteType: string): Observable<ApiEnvelope<ActivationStatus>> {
    return this._cS.API_POST(AuthEndpoints.ActivationResend, { token, siteType }, false);
  }

  setPassword(token: string, password: string, confirmPassword: string, finish: ActivationFinish): Observable<ApiEnvelope<ActivationStatus>> {
    return this._cS.API_POST(AuthEndpoints.ActivationSetPassword, { token, password, confirmPassword, ...finish }, false);
  }

  passkeyOptions(token: string): Observable<ApiEnvelope<PasskeyOptionsResult>> {
    return this._cS.API_POST(AuthEndpoints.ActivationPasskeyOptions, { token }, false);
  }

  passkeyComplete(token: string, challengeId: string, attestationResponseJson: string, friendlyName: string, finish: ActivationFinish): Observable<ApiEnvelope<ActivationStatus>> {
    return this._cS.API_POST(AuthEndpoints.ActivationPasskeyComplete, { token, challengeId, attestationResponseJson, friendlyName, ...finish }, false);
  }
}


export type SsoTarget = 'PAYBEV' | 'SUPPLIER' | 'BROOKLYN';

/**
 * Cross-portal sign-in. The portal you are in asks the API for a single-use code (60 s) bound to its
 * session; the code, never a refresh token, goes into the other portal's URL as `t`.
 */
@Injectable({
  providedIn: 'root',
})
export class SsoHandoffService {
  constructor(private _cS: CommonService) {}

  /** Base64 without padding, the encoding the receiving shells already strip and decode. */
  static encodeEmail(email: string): string {
    return btoa(unescape(encodeURIComponent(email))).replace(/=+$/, '');
  }

  requestCode(target: SsoTarget): Observable<string | null> {
    return this._cS.API_POST(AuthEndpoints.SsoHandoff, { target }, false).pipe(
      map((response: ApiEnvelope<{ code: string }>) =>
        response.responseStatus == ResponseStatus.success && response.result?.code ? response.result.code : null
      )
    );
  }

  /** Opens the tab synchronously (so popup blockers allow it) and points it at the URL once the code arrives. */
  openInNewTab(target: SsoTarget, buildUrl: (code: string) => string, onFailure?: () => void): void {
    const tab = window.open('', '_blank');
    this.requestCode(target).subscribe({
      next: (code) => {
        if (code && tab) {
          tab.location.href = buildUrl(code);
        } else {
          tab?.close();
          onFailure?.();
        }
      },
      error: () => {
        tab?.close();
        onFailure?.();
      },
    });
  }

  /** Leaves the current portal for the target one. */
  navigate(target: SsoTarget, buildUrl: (code: string) => string, onFailure?: () => void): void {
    this.requestCode(target).subscribe({
      next: (code) => (code ? (window.location.href = buildUrl(code)) : onFailure?.()),
      error: () => onFailure?.(),
    });
  }
}

interface RefreshResponse {
  responseStatus: ResponseStatus;
  message?: string;
  result?: { token: string; refreshToken: string };
}

/** True when the JWT's `exp` has passed (or cannot be read), allowing a small clock skew. */
export function isJwtExpired(token: string, skewSeconds = 30): boolean {
  const payload = token.split('.')[1];
  if (!payload) {
    return true;
  }
  try {
    const claims: { exp?: unknown } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof claims.exp !== 'number' || claims.exp * 1000 <= Date.now() + skewSeconds * 1000;
  } catch {
    return true;
  }
}

@Injectable({
  providedIn: 'root',
})
export class TokenRefreshService {
  private inFlight: Observable<string> | null = null;

  constructor(private http: HttpClient, private authService: AuthServiceService) {}

  /** Exchanges the stored refresh token for a new access token. Concurrent callers share one request. */
  refresh(): Observable<string> {
    if (this.inFlight) {
      return this.inFlight;
    }
    const jwtToken = localStorage.getItem('BearerToken');
    const refreshToken = localStorage.getItem('RefreshToken');
    if (!jwtToken || !refreshToken) {
      return throwError(() => new Error('No session to refresh.'));
    }

    this.inFlight = this.http
      .post<RefreshResponse>(`${environment.apiURL}${AuthEndpoints.Refresh}`, { jwtToken, refreshToken })
      .pipe(
        map((response) => {
          if (response.responseStatus !== ResponseStatus.success || !response.result) {
            throw new Error(response.message || 'Refresh failed.');
          }
          localStorage.setItem('BearerToken', response.result.token);
          localStorage.setItem('RefreshToken', response.result.refreshToken);
          this.authService.scheduleAutoLogout();
          return response.result.token;
        }),
        finalize(() => (this.inFlight = null)),
        shareReplay(1)
      );
    return this.inFlight;
  }
}
